/**
 * React Context and Provider for i18n
 * Provides translations to all components and handles language detection
 */

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { detectLanguage, setLocale as setGlobalLocale, type SupportedLocale, type Translations } from "./index";
import { en } from "./locales/en";
import { fr } from "./locales/fr";
import { it } from "./locales/it";
import { de } from "./locales/de";
import { es } from "./locales/es";
import { UserService } from "../services/UserService";

const translations: Record<SupportedLocale, Translations> = { en, fr, it, de, es };

interface I18nContextType {
  locale: SupportedLocale;
  t: Translations;
  setLocale: (locale: SupportedLocale) => void;
}

const I18nContext = createContext<I18nContextType | null>(null);

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectLanguage());

  // Sync global locale when state changes
  const setLocale = (newLocale: SupportedLocale) => {
    setGlobalLocale(newLocale);
    setLocaleState(newLocale);
  };

  // Initialize locale: try API user lang, fallback to Office/browser detection
  useEffect(() => {
    let cancelled = false;

    UserService.getUserLang().then((apiLang) => {
      if (!cancelled && apiLang) {
        setGlobalLocale(apiLang);
        setLocaleState(apiLang);
      }
    });

    return () => { cancelled = true; };
  }, []);

  const value = useMemo<I18nContextType>(
    () => ({
      locale,
      t: translations[locale],
      setLocale,
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access translations in components
 *
 * @example
 * const { t } = useTranslation();
 * return <Button>{t.ui.addRoom}</Button>;
 */
export function useTranslation() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }

  return context;
}

export { I18nContext };
